export const gameTheoryPage = Object.freeze({
  path: "/game-theory",
  navLabel: "Game Theory",
  seo: {
    titleBase: "Game Theory",
    description: "Learn how RaizIQ players seed markets, chase live totals, rally others, and time moves before market close."
  },
  hero: {
    eyebrow: "Game theory",
    title: "How players move markets.",
    description: "Every vote changes what the next player sees. A quiet market asks someone to start it. A moving market gives everyone a target, a reason to rally, and a reason to come back before the clock runs out.",
    primaryCta: { label: "Enter the live board", to: "/markets" },
    secondaryCta: { label: "Create a market", to: "/create" }
  },
  chase: {
    eyebrow: "The chase",
    title: "Visible SOL changes the next move.",
    description: "Players react to the board in front of them. Each move creates a new target, a new story, and a new reason for someone else to answer.",
    callout: "The winner is not automatically the last voter. The winning option is the option with the largest finalized escrow total at market close. Winning-option voters share the remaining escrow proportional to their finalized winning-option contribution.",
    steps: [
      { player: "Player A", title: "Starts the market", option: "Option A", amount: "1.00 SOL", body: "Now everyone has a target." },
      { player: "Player B", title: "Sees the opening", option: "Option B", amount: "1.10 SOL", body: "A small overtake can change the leader." },
      { player: "Player C", title: "Sees two sides funded", option: "Option C", amount: "1.20 SOL", body: "The pool is larger and the board looks alive." },
      { player: "Player D", title: "Makes every side matter", option: "Option D", amount: "1.30 SOL", body: "Each side has money, pride, and a reason to recruit." },
      { player: "A closer", title: "Enters late", option: "Any option", amount: "2.00 SOL", body: "The bigger the board gets, the more valuable timing and recruitment become." }
    ]
  },
  seeding: {
    eyebrow: "Seeding",
    title: "Sometimes the first move is making every option look alive.",
    body: "A creator or early player may vote 0.25 SOL into each option. That does not pick a side. It creates a board where every side has something to chase. Empty options feel dead. Seeded options give later players a reason to compare, overtake, and recruit.",
    caveat: "Seeding all options also funds the sides you may later compete against. The point is to start activity, not guarantee an outcome."
  },
  earlyVsLate: {
    eyebrow: "Timing",
    title: "A late vote can win a board. Early recruitment makes the board worth winning.",
    columns: [
      { title: "Late swing only", items: ["Wait until the end.", "Vote enough to lead.", "Useful if the pool is already worth fighting for.", "Weak if nobody else joined."] },
      { title: "Early rally", items: ["Start or chase early.", "Share the market.", "Bring people into your side.", "Build a bigger pool before the final move."] }
    ]
  },
  mindsets: [
    { title: "The Seeder", body: "Starts the board so other players have something to chase." },
    { title: "The Chaser", body: "Looks for a leader they can overtake with a focused move." },
    { title: "The Recruiter", body: "Knows the best market is the one with more people in it, so they bring a group early." },
    { title: "The Counterpuncher", body: "Waits for momentum to form, then moves a competing side." },
    { title: "The Closer", body: "Acts late, but only after the market has enough total SOL to justify the move." }
  ],
  watchItems: [
    { title: "Option totals", body: "Which side is currently leading." },
    { title: "Percentages", body: "How much of the market each option controls." },
    { title: "Vote history", body: "Whether momentum is fresh or stale." },
    { title: "End time", body: "How much time remains for a response." },
    { title: "Leaderboards", body: "Who has been moving markets." },
    { title: "Share links and comments", body: "Where recruitment happens." }
  ],
  rules: [
    "Highest finalized escrow total wins.",
    "Votes must finalize before market close.",
    "Ties use the existing tie-break order.",
    "Winning-option voters share the remaining escrow proportionally.",
    "Fees, reserves, rent, and terms affect final settlement.",
    "This page explains strategy, not promised outcomes."
  ],
  finalCta: {
    title: "Find a board worth moving.",
    body: "Look for a market with visible tension, pick the side you can rally, and decide whether you are early enough to build the pool or late enough to close it.",
    links: [
      { label: "Browse live markets", to: "/markets", variant: "primary" },
      { label: "Create a market", to: "/create", variant: "secondary" },
      { label: "Read the FAQ", to: "/faq", variant: "secondary" }
    ]
  }
});
